import { supabase } from "@/integrations/supabase/client";
import { toEdgeFunctionFailure } from "@/utils/userFacingError";

export type JournalEntryItem = {
  account_number: string
  debit: number
  credit: number
  description: string | null
  line_order: number
}

export type JournalEntryWithSource = {
  entry: {
    id: string
    journal_number: number
    title: string
    date: string
    fiscal_year_id: string
    currency?: string
    created_at?: string | null
  }
  sourceType: string
  sourceRefId: string | null
  originalSeries: string | null
  originalNumber: number | null
  regDate: string | null
  sieAttachmentId: string | null
  accounts: string[]
}

/**
 * FIN-1023 D2/D3/AC-6 — present only when this journal entry is a system-generated closing
 * entry whose document went through FX conversion. `fxSettlementSource` names which rule
 * produced `settledSekAmount`: 'real_sek_debit' (a real SEK-account bank transaction, a fact),
 * 'settlement_day_rate' (a foreign-currency-account transaction converted at the settlement-day
 * rate) or 'user_entered' (an external/manual settlement with no real transaction — the
 * consultant-facing fallback warning case).
 */
export type ClosingFxInfo = {
  bookedSekAmount: number | null
  bookedExchangeRate: number | null
  bookedExchangeRateDate: string | null
  settledSekAmount: number | null
  settledExchangeRate: number | null
  settledExchangeRateDate: string | null
  fxSettlementSource: 'real_sek_debit' | 'settlement_day_rate' | 'user_entered' | null
}

export type JournalEntryDetail = {
  entry: JournalEntryWithSource['entry']
  items: JournalEntryItem[]
  originalSeries: string | null
  originalNumber: number | null
  documentFilePath: string | null
  documentFileName: string | null
  closingFx: ClosingFxInfo | null
}

export type JournalEntriesWithSourceResult = {
  data: JournalEntryWithSource[]
  totalItems: number
}

export async function fetchJournalEntriesWithSource(opts: {
  /** One entry by id, whatever its fiscal year; the result holds it or nothing (RLS). */
  entryId?: string
  fiscalYearId?: string
  sourceType?: string
  /** SIE verification series, e.g. "V" */
  series?: string
  page?: number
  pageSize?: number
}): Promise<JournalEntriesWithSourceResult> {
  const { data, error } = await supabase.functions.invoke('journal-entry-api', {
    body: { action: 'getJournalEntriesWithSource', ...opts },
  })
  if (error) throw await toEdgeFunctionFailure(error, 'fetchJournalEntriesWithSource')
  return data as JournalEntriesWithSourceResult
}

export async function fetchJournalEntryWithItems(journalEntryId: string): Promise<JournalEntryDetail> {
  const { data, error } = await supabase.functions.invoke('journal-entry-api', {
    body: { action: 'getJournalEntryWithItems', journalEntryId },
  })
  if (error) throw await toEdgeFunctionFailure(error, 'fetchJournalEntryWithItems')
  return data as JournalEntryDetail
}
